import { AlertTriangle, Ban } from "lucide-react";
import type {
  BuyerInterviewDto,
  BuyerInterviewFlagDto,
  BuyerInterviewOutcomeDto,
  BuyerInterviewQuestionDto,
  BuyerInterviewRegistryDto,
} from "@/api/buyers";
import { StatusBadge } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { buyerLabel } from "../buyerFilters";
import { verdictTone } from "../buyerListBits";
import {
  DIMENSION_ORDER,
  DIMENSION_SECTION,
  describeAnswer,
  dimensionLabel,
  isAsked,
  isRecorded,
  questionsForSection,
  sectionTitle,
  toDraftAnswers,
  type DraftAnswers,
} from "./interviewBits";
import { flagsByQuestion, type InterviewFlagNote } from "./interviewFlags";

/**
 * A COMPLETED interview, read-only, with the answers that drove its result
 * marked where they sit.
 *
 * <p>🔴 <b>This is NOT the live interview's review step</b>, and that is why it
 * is a separate component rather than an option on `InterviewTranscript`. A
 * conduct session must never show a flag — it would be a rating hint reaching
 * the specialist mid-call — so the transcript it uses has nowhere to put one.
 * Every flag here comes from the server's `questionIds`; nothing is re-derived.</p>
 *
 * <p>Questions are shown in the wording they were ASKED with: the caller passes
 * the registry rendered in this interview's own `wordingVersion` (0 = the coded
 * defaults), and the interview stores option keys, which that registry turns
 * back into the labels that were on screen. Section headings are that
 * version's section titles (ruling Q3); the outcome and the flag lines keep the
 * coded dimension names. A stored key the registry no longer offers renders as
 * the raw key, marked.</p>
 */
export function InterviewRecord({
  interview,
  registry,
  idPrefix = "interview-record",
}: {
  interview: BuyerInterviewDto;
  registry: BuyerInterviewRegistryDto;
  /** Scroll targets are `${idPrefix}-q-${questionId}`. */
  idPrefix?: string;
}) {
  const outcome = interview.outcome ?? null;
  const answers = toDraftAnswers(interview.answers);
  const flags = flagsByQuestion(outcome);
  const anchorId = (questionId: string) => `${idPrefix}-q-${questionId}`;
  const questionsById = new Map(registry.questions.map((q) => [q.id, q]));
  // A flag can only point at a question that was asked. A branch that never
  // opened has no row, and neither has a retired question nothing was recorded
  // against, so a link to either would scroll nowhere.
  const asked = (id: string) => {
    const q = questionsById.get(id);
    return q != null && isAsked(q, answers);
  };

  return (
    <div className="grid gap-space-4" data-testid="interview-record">
      {outcome && <OverrideDetails outcome={outcome} />}

      {outcome && (
        <>
          <TopFlagList
            title="Knockouts"
            flags={outcome.knockouts}
            icon={Ban}
            tone="text-status-error"
            testId="interview-record-knockouts"
            asked={asked}
            anchorId={anchorId}
          />
          <TopFlagList
            title="Things that do not add up"
            flags={outcome.coherenceFlags}
            icon={AlertTriangle}
            tone="text-status-warning"
            testId="interview-record-coherence"
            asked={asked}
            anchorId={anchorId}
          />
        </>
      )}

      <p className="text-caption text-gray-mid m-0" data-testid="interview-record-wording">
        Questions are shown in the wording they were asked with. The recorded
        answers are exactly as written down.
      </p>

      {DIMENSION_ORDER.map((dimension) => {
        const sectionId = DIMENSION_SECTION[dimension];
        const result = outcome?.dimensions.find((d) => d.dimension === dimension);
        const visible = questionsForSection(registry, sectionId).filter((q) =>
          isAsked(q, answers),
        );
        const flagged = result && (result.verdict === "Fail" || result.verdict === "Concern");
        // Causes that live in ANOTHER section: without a pointer, a Concern
        // here would sit above a list of answers none of which is marked.
        const crossSection = flagged
          ? (result.questionIds ?? []).filter((id) => {
              const q = questionsById.get(id);
              return q != null && q.sectionId !== sectionId && asked(id);
            })
          : [];

        return (
          <section
            key={dimension}
            className="grid gap-space-2"
            data-testid={`interview-record-section-${dimension}`}
          >
            <div className="flex flex-wrap items-center gap-space-2">
              <h3 className="font-display font-bold text-small text-onyx m-0">
                {sectionTitle(registry, sectionId)}
              </h3>
              {result && (
                <StatusBadge tone={verdictTone(result.verdict)}>
                  {buyerLabel(result.verdict)}
                </StatusBadge>
              )}
            </div>
            {result && (
              <p className="text-caption text-gray-mid m-0">{result.ruleText}</p>
            )}
            {crossSection.map((id) => {
              const q = questionsById.get(id)!;
              return (
                <p
                  key={id}
                  className="text-caption text-gray-mid m-0"
                  data-testid="interview-record-see-also"
                >
                  See:{" "}
                  <button
                    type="button"
                    onClick={() => scrollToAnswer(anchorId(id))}
                    className="text-gold-shadow hover:underline text-left"
                  >
                    {q.label}
                  </button>{" "}
                  under {sectionTitle(registry, q.sectionId)}
                </p>
              );
            })}

            <dl className="grid gap-space-2 m-0">
              {visible.map((question) => (
                <RecordRow
                  key={question.id}
                  id={anchorId(question.id)}
                  question={question}
                  answers={answers}
                  notes={flags.get(question.id) ?? []}
                />
              ))}
            </dl>
          </section>
        );
      })}
    </div>
  );
}

/** Scroll an answer into view and move focus to it, so a keyboard user lands
 *  where a sighted user's eye does. `scrollIntoView` is absent in jsdom. */
export function scrollToAnswer(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  el.focus({ preventScroll: true });
}

function RecordRow({
  id,
  question,
  answers,
  notes,
}: {
  id: string;
  question: BuyerInterviewQuestionDto;
  answers: DraftAnswers;
  notes: InterviewFlagNote[];
}) {
  const answer = answers[question.id];
  const recorded = isRecorded(answer);
  return (
    <div
      id={id}
      tabIndex={-1}
      className="border-b border-border-light pb-space-2 last:border-b-0 outline-none focus:ring-2 focus:ring-gold"
      data-testid={`interview-record-row-${question.id}`}
    >
      <dt className="text-caption text-gray-mid">
        {question.label}
        {question.verbatim && (
          <span className="ml-space-2 uppercase tracking-wide">· in their words</span>
        )}
      </dt>
      <dd className="m-0 mt-space-1">
        <span
          className={
            recorded
              ? "block text-small text-onyx whitespace-pre-wrap"
              : "block text-small text-gray-mid italic"
          }
        >
          {recorded ? describeAnswer(question, answer) : "Not recorded"}
        </span>
        {notes.length > 0 && (
          <ul className="m-0 mt-space-1 pl-0 list-none grid gap-space-1">
            {notes.map((note) => (
              <FlagNoteLine key={`${note.kind}-${note.source}`} note={note} />
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}

function FlagNoteLine({ note }: { note: InterviewFlagNote }) {
  const Icon = note.kind === "knockout" ? Ban : AlertTriangle;
  const tone =
    note.kind === "knockout" || note.kind === "fail"
      ? "text-status-error"
      : "text-status-warning";
  return (
    <li
      className="flex items-start gap-space-2 text-caption text-onyx"
      data-testid="interview-flag-note"
      data-flag-kind={note.kind}
    >
      <Icon size={13} className={`${tone} mt-[3px] shrink-0`} aria-hidden />
      <span>{flagNoteText(note)}</span>
    </li>
  );
}

export function flagNoteText(note: InterviewFlagNote): string {
  switch (note.kind) {
    case "fail":
      return `Counts toward Fail in ${dimensionLabel(note.dimension ?? "")}`;
    case "concern":
      return `Counts toward Concern in ${dimensionLabel(note.dimension ?? "")}`;
    case "knockout":
      return `Knockout: ${note.text ?? ""}`;
    case "coherence":
      return `Does not add up: ${note.text ?? ""}`;
  }
}

function TopFlagList({
  title,
  flags,
  icon: Icon,
  tone,
  testId,
  asked,
  anchorId,
}: {
  title: string;
  flags: BuyerInterviewFlagDto[];
  icon: typeof Ban;
  tone: string;
  testId: string;
  asked: (id: string) => boolean;
  anchorId: (id: string) => string;
}) {
  if (flags.length === 0) return null;
  return (
    <div data-testid={testId}>
      <h3 className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-1">
        {title}
      </h3>
      <ul className="m-0 pl-0 list-none grid gap-space-1">
        {flags.map((f) => {
          const target = (f.questionIds ?? []).find(asked);
          return (
            <li key={f.code} className="flex items-start gap-space-2 text-small text-onyx">
              <Icon size={13} className={`${tone} mt-[3px] shrink-0`} aria-hidden />
              {target ? (
                <button
                  type="button"
                  onClick={() => scrollToAnswer(anchorId(target))}
                  className="text-left hover:underline"
                  data-testid={`${testId}-item`}
                >
                  {f.text}
                </button>
              ) : (
                <span>{f.text}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The override stored on THIS version: the computed result, what it was
 *  changed to, by whom, when, and why. Overrides overwrite one another, so this
 *  is the current one — there is no override history to show. */
function OverrideDetails({ outcome }: { outcome: BuyerInterviewOutcomeDto }) {
  const { dateTime } = useDateFormatter();
  if (outcome.overriddenVerdict == null) return null;
  return (
    <div
      className="rounded-md border border-border-light p-space-3 grid gap-space-2"
      data-testid="interview-record-override"
    >
      <h3 className="text-caption uppercase tracking-wide text-gray-mid m-0">
        Result overridden
      </h3>
      <dl className="grid gap-space-1 m-0 text-small">
        <OverrideField label="Original result">
          <StatusBadge tone={verdictTone(outcome.computedVerdict)}>
            {buyerLabel(outcome.computedVerdict)}
          </StatusBadge>
        </OverrideField>
        <OverrideField label="New result">
          <StatusBadge tone={verdictTone(outcome.overriddenVerdict)}>
            {buyerLabel(outcome.overriddenVerdict)}
          </StatusBadge>
        </OverrideField>
        <OverrideField label="By">
          {outcome.overriddenByUserName || "Not recorded"}
        </OverrideField>
        <OverrideField label="When">
          {outcome.overriddenAtUtc ? dateTime(outcome.overriddenAtUtc) : "Not recorded"}
        </OverrideField>
        <OverrideField label="Note">
          <span className="whitespace-pre-wrap">
            {outcome.overrideReason || "No note recorded"}
          </span>
        </OverrideField>
      </dl>
    </div>
  );
}

function OverrideField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start gap-space-2">
      <dt className="w-32 shrink-0 text-caption uppercase tracking-wide text-gray-mid">
        {label}
      </dt>
      <dd className="m-0 min-w-0 flex-1 text-onyx">{children}</dd>
    </div>
  );
}
